import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Monitor, Play, Pause, RotateCcw, Eye, EyeOff, Tv,
  ChevronDown, Zap, MessageSquare, Camera, Radio, Keyboard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BroadcastOverlayPage = ({ token, onBack }) => {
  const [races, setRaces] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [broadcastState, setBroadcastState] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const lastValidBroadcast = useRef(null);
  const [chromaKey, setChromaKey] = useState("green");
  const [showOverlay, setShowOverlay] = useState(true);
  const [showCommentary, setShowCommentary] = useState(true);
  const [showRunnerTags, setShowRunnerTags] = useState(true);
  const [showSignals, setShowSignals] = useState(true);
  const [frozenFrame, setFrozenFrame] = useState(false);
  const [featuredRunner, setFeaturedRunner] = useState(null);
  const [commentary, setCommentary] = useState(null);
  const [replayMode, setReplayMode] = useState(false);
  const [replayData, setReplayData] = useState(null);
  const [showControls, setShowControls] = useState(true);
  const [showHotkeyRef, setShowHotkeyRef] = useState(false);

  const streamIntervalRef = useRef(null);
  const authHeaders = { Authorization: `Bearer ${token}` };

  // Fetch races
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/races/current`, { headers: authHeaders });
        setRaces(res.data);
        if (res.data.length > 0) setSelectedRace(res.data[0].race_id);
      } catch (e) {}
    };
    load();
  }, []);

  // Fetch broadcast state — with cached fallback
  const fetchBroadcastState = useCallback(async () => {
    if (!selectedRace || frozenFrame) return;
    try {
      const res = await axios.get(`${API}/broadcast/state/${selectedRace}`, { headers: authHeaders });
      setBroadcastState(res.data);
      lastValidBroadcast.current = res.data;
    } catch (e) {
      if (lastValidBroadcast.current) setBroadcastState(lastValidBroadcast.current);
    }
  }, [selectedRace, frozenFrame]);

  useEffect(() => { if (selectedRace) fetchBroadcastState(); }, [selectedRace]);

  // No auto-start — presenter clicks GO LIVE when ready

  // Streaming
  const startStreaming = useCallback(() => {
    setIsStreaming(true);
    setReplayMode(false);
    streamIntervalRef.current = setInterval(fetchBroadcastState, 800);
  }, [fetchBroadcastState]);

  const stopStreaming = useCallback(() => {
    setIsStreaming(false);
    if (streamIntervalRef.current) {
      clearInterval(streamIntervalRef.current);
      streamIntervalRef.current = null;
    }
  }, []);

  const fetchReplayInsights = useCallback(async () => {
    if (!selectedRace) return;
    stopStreaming();
    try {
      const res = await axios.get(`${API}/broadcast/replay/${selectedRace}`, { headers: authHeaders });
      setReplayData(res.data);
      setReplayMode(true);
    } catch (e) {}
  }, [selectedRace, stopStreaming]);

  const resetRace = useCallback(async () => {
    if (!selectedRace) return;
    stopStreaming();
    try {
      await axios.post(`${API}/admin/reset?race_id=${selectedRace}`, {}, { headers: authHeaders });
      setReplayMode(false);
      setReplayData(null);
      await fetchBroadcastState();
      toast.success("Race reset");
    } catch (e) {}
  }, [selectedRace, stopStreaming, fetchBroadcastState]);

  // Generate AI commentary
  const generateCommentary = useCallback(async () => {
    if (!selectedRace) return;
    try {
      const res = await axios.post(`${API}/broadcast/commentary`, { race_id: selectedRace }, { headers: authHeaders });
      setCommentary(res.data);
    } catch (e) {}
  }, [selectedRace]);

  useEffect(() => {
    if (isStreaming && broadcastState) {
      const interval = setInterval(generateCommentary, 8000);
      return () => clearInterval(interval);
    }
  }, [isStreaming, broadcastState, generateCommentary]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      switch (e.key.toLowerCase()) {
        case " ": e.preventDefault(); isStreaming ? stopStreaming() : startStreaming(); break;
        case "g": e.preventDefault(); if (!isStreaming) startStreaming(); break;
        case "s": e.preventDefault(); if (isStreaming) stopStreaming(); break;
        case "r": e.preventDefault(); resetRace(); break;
        case "p": e.preventDefault(); fetchReplayInsights(); break;
        case "f": e.preventDefault(); setFrozenFrame(p => !p); break;
        case "o": e.preventDefault(); setShowOverlay(p => !p); break;
        case "c": e.preventDefault(); setShowCommentary(p => !p); break;
        case "i": e.preventDefault(); setShowSignals(p => !p); break;
        case "t": e.preventDefault(); setShowRunnerTags(p => !p); break;
        case "k": e.preventDefault(); setChromaKey(p => p === "green" ? "black" : "green"); break;
        case "x": e.preventDefault(); setShowControls(p => !p); break;
        case "1": case "2": case "3": case "4": case "5": case "6": case "7": case "8": case "9":
          e.preventDefault();
          if (broadcastState?.runners) {
            const pos = parseInt(e.key);
            const runner = broadcastState.runners.find(r => r.position === pos);
            if (runner) setFeaturedRunner(p => p === runner.number ? null : runner.number);
          }
          break;
        case "0": e.preventDefault(); setFeaturedRunner(null); break;
        case "h": case "?": e.preventDefault(); setShowHotkeyRef(p => !p); break;
        case "escape": showHotkeyRef ? setShowHotkeyRef(false) : onBack?.(); break;
        default: break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isStreaming, broadcastState, showHotkeyRef, onBack, startStreaming, stopStreaming, resetRace, fetchReplayInsights]);

  useEffect(() => () => { if (streamIntervalRef.current) clearInterval(streamIntervalRef.current); }, []);

  const bgColour = chromaKey === "green" ? "#00FF00" : "#000000";
  const d = broadcastState;

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: bgColour }} data-testid="broadcast-overlay-page">

      {/* Producer Controls (toggleable with X) */}
      {showControls && (
        <div className="absolute top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-white/10 px-4 h-12 flex items-center justify-between" data-testid="producer-controls">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Radio className="w-3 h-3 text-[#F97316]" />
              <span className="text-white text-xs font-bold uppercase tracking-wider">Broadcast</span>
            </div>
            <Select value={selectedRace || ""} onValueChange={setSelectedRace}>
              <SelectTrigger className="w-[180px] h-7 bg-black/50 border-white/20 text-white text-xs">
                <SelectValue placeholder="Select Race" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1d22] border-white/20">
                {races.map(r => <SelectItem key={r.race_id} value={r.race_id} className="text-white text-xs">{r.track_name} · {r.distance_m}m</SelectItem>)}
              </SelectContent>
            </Select>

            <Button onClick={isStreaming ? stopStreaming : startStreaming} size="sm" className={`h-7 text-xs ${isStreaming ? 'bg-red-600 text-white' : 'bg-[#10B981] text-white'}`}>
              {isStreaming ? <><Pause className="w-3 h-3 mr-1" /> STOP</> : <><Play className="w-3 h-3 mr-1" /> GO LIVE</>}
            </Button>
            <Button onClick={resetRace} variant="ghost" size="sm" className="h-7 text-white/40 hover:text-white"><RotateCcw className="w-3 h-3" /></Button>
          </div>
          <div className="flex items-center gap-1">
            <ChipBtn active={showOverlay} onClick={() => setShowOverlay(!showOverlay)} label="OVR" />
            <ChipBtn active={showCommentary} onClick={() => setShowCommentary(!showCommentary)} label="COM" />
            <ChipBtn active={showRunnerTags} onClick={() => setShowRunnerTags(!showRunnerTags)} label="TAG" />
            <ChipBtn active={showSignals} onClick={() => setShowSignals(!showSignals)} label="SIG" />
            <ChipBtn active={frozenFrame} onClick={() => setFrozenFrame(!frozenFrame)} label="FRZ" colour="#F97316" />
            <Button variant="ghost" size="sm" className="h-7 text-white/30 hover:text-white text-xs" onClick={() => setChromaKey(chromaKey === "green" ? "black" : "green")}>
              {chromaKey === "green" ? "GRN" : "BLK"}
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-white/30 hover:text-white" onClick={() => setShowHotkeyRef(!showHotkeyRef)}><Keyboard className="w-3 h-3" /></Button>
            <Link to="/">
              <Button variant="ghost" size="sm" className="h-7 text-white/30 hover:text-white text-xs" data-testid="broadcast-back-hub">Hub</Button>
            </Link>
          </div>
        </div>
      )}

      {/* === BROADCAST GRAPHICS LAYER === */}
      {d && showOverlay && (
        <>
          {/* TOP BANNER */}
          <div className="absolute top-14 left-0 right-0 z-30" data-testid="broadcast-top-banner">
            <div className="mx-8 flex items-stretch h-10 rounded-md overflow-hidden shadow-lg">
              {/* Race ID */}
              <div className="bg-[#F97316] px-4 flex items-center">
                <span className="text-white text-xs font-black tracking-wider uppercase">{d.track_name}</span>
              </div>
              {/* State chips */}
              <div className="bg-black/90 flex items-center gap-4 px-4 flex-1">
                <BroadcastChip label="PACE" value={d.race_state || "EVEN"} />
                <BroadcastChip label="PRESSURE" value={d.pressure_state || "LOW"} />
                <BroadcastChip label="BIAS" value={d.bias_state || "NEUTRAL"} />
                {d.leader_status && <BroadcastChip label="LEADER" value={d.leader_status} />}
              </div>
              {/* LIVE indicator */}
              {isStreaming && (
                <div className="bg-red-600 px-3 flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  <span className="text-white text-[10px] font-black tracking-wider">LIVE</span>
                </div>
              )}
            </div>
          </div>

          {/* LEFT CALLOUT STACK */}
          {showSignals && d.signals && (
            <div className="absolute top-28 left-8 z-30 space-y-1.5 max-w-[280px]" data-testid="broadcast-callouts">
              {d.signals?.slice(0, 4).map((signal, i) => (
                <div key={i} className="bg-black/85 backdrop-blur-sm px-3 py-1.5 rounded-md border-l-2 border-[#F97316]">
                  <div className="text-white text-xs font-bold">{typeof signal === 'string' ? signal : signal.label || signal.text || JSON.stringify(signal)}</div>
                </div>
              ))}
            </div>
          )}

          {/* RUNNER TAGS (positioned on broadcast area) */}
          {showRunnerTags && d.runners && (
            <div className="absolute inset-0 z-20 pointer-events-none" data-testid="broadcast-runner-tags">
              {d.runners.map((runner, i) => {
                const isFeatured = featuredRunner === runner.number;
                // Spread runners across the broadcast canvas
                const x = 15 + (runner.position - 1) * (70 / Math.max(d.runners.length - 1, 1));
                const y = 40 + (i % 3) * 8 + Math.sin(i * 1.2) * 5;
                return (
                  <div
                    key={runner.number}
                    className="absolute transition-all duration-500"
                    style={{ left: `${x}%`, top: `${y}%` }}
                  >
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-md ${
                      isFeatured ? 'bg-[#F97316] shadow-[0_0_12px_rgba(249,115,22,0.5)]' : 'bg-black/80'
                    }`}>
                      <span className="text-white text-[10px] font-black">{runner.number}</span>
                      <span className="text-white/80 text-[10px]">{(runner.name || runner.horse_name || '').split(' ').pop()}</span>
                      <span className={`text-[9px] font-bold ${
                        runner.efficiency === 'Strong' ? 'text-[#10B981]' : runner.efficiency === 'Weak' ? 'text-[#EF4444]' : 'text-white/40'
                      }`}>
                        {runner.position}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* LOWER-THIRD COMMENTARY STRIP */}
          {showCommentary && commentary && (
            <div className="absolute bottom-24 left-0 right-0 z-30" data-testid="broadcast-commentary">
              <div className="mx-8">
                <div className="bg-black/90 backdrop-blur-sm rounded-md overflow-hidden">
                  <div className="flex items-stretch">
                    <div className="bg-[#F97316] px-3 flex items-center">
                      <MessageSquare className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="px-4 py-2 flex-1">
                      <p className="text-white text-xs leading-relaxed">{commentary.commentary || commentary.text}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FOOTER TRACK VISUAL — simulates live media feed */}
          <div className="absolute bottom-0 left-0 right-0 z-30 h-16" data-testid="broadcast-track-footer">
            <div className="mx-8 h-full bg-black/90 backdrop-blur-sm rounded-t-md overflow-hidden border-t border-white/10">
              <svg viewBox="0 0 1200 52" className="w-full h-full" preserveAspectRatio="none">
                {/* Track rail */}
                <rect x="60" y="8" width="1080" height="36" rx="3" fill="rgba(255,255,255,0.03)" />
                <line x1="60" y1="26" x2="1140" y2="26" stroke="rgba(255,255,255,0.06)" strokeDasharray="12 6" />
                {/* Finish */}
                <line x1="1130" y1="8" x2="1130" y2="44" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeDasharray="3 2" />
                {/* Runners */}
                {d.runners?.map((runner, i) => {
                  const totalRunners = d.runners?.length || 1;
                  const px = 70 + (totalRunners - runner.position) / Math.max(totalRunners - 1, 1) * 1050;
                  const py = 14 + (i % 3) * 12;
                  const isFeatured = featuredRunner === runner.number;
                  const effColour = runner.efficiency === "Strong" ? "#10B981" : runner.efficiency === "Weak" ? "#EF4444" : "#F59E0B";
                  return (
                    <g key={runner.number}>
                      <circle cx={px} cy={py} r={isFeatured ? 5 : 3.5} fill={isFeatured ? "#F97316" : effColour} opacity={0.9}>
                        {isFeatured && <animate attributeName="r" values="5;7;5" dur="1s" repeatCount="indefinite" />}
                      </circle>
                      <text x={px + 7} y={py + 3} fill="rgba(255,255,255,0.6)" fontSize="7" fontWeight="bold" fontFamily="monospace">{runner.number}</text>
                    </g>
                  );
                })}
                {/* Distance label */}
                <text x="20" y="30" fill="rgba(255,255,255,0.15)" fontSize="8" fontFamily="monospace">START</text>
                <text x="1140" y="30" fill="rgba(255,255,255,0.15)" fontSize="8" fontFamily="monospace">FIN</text>
              </svg>
            </div>
          </div>

          {/* POST-RACE HIGHLIGHT STRIP */}
          {replayMode && replayData && (
            <div className="absolute bottom-28 left-0 right-0 z-30" data-testid="broadcast-replay-strip">
              <div className="mx-8">
                <div className="bg-black/90 rounded-md p-3">
                  <div className="text-[#F97316] text-[10px] font-black tracking-wider uppercase mb-2">POST-RACE HIGHLIGHTS</div>
                  <div className="grid grid-cols-4 gap-2">
                    <ReplayChip label="Best Run" value={`#${replayData.best_run?.number || '—'} ${replayData.best_run?.name || ''}`} />
                    <ReplayChip label="Late Speed" value={replayData.emerging_threat || "—"} />
                    <ReplayChip label="Bias Impact" value={replayData.bias_state || "Neutral"} />
                    <ReplayChip label="Pressure Load" value={replayData.pressure_state || "—"} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!d && !replayMode && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center text-white/30">
            <Tv className="w-20 h-20 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-heading uppercase">Select a race to begin</p>
            <p className="text-xs mt-2">Press <kbd className="px-1 py-0.5 rounded bg-white/10 text-xs font-mono">H</kbd> for hotkeys · <kbd className="px-1 py-0.5 rounded bg-white/10 text-xs font-mono">X</kbd> to toggle controls</p>
          </div>
        </div>
      )}

      {/* Hotkey Reference */}
      {showHotkeyRef && <HotkeyPanel onClose={() => setShowHotkeyRef(false)} />}
    </div>
  );
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

const ChipBtn = ({ active, onClick, label, colour }) => (
  <button
    onClick={onClick}
    className={`h-7 px-2 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${
      active ? 'text-white' : 'text-white/30'
    }`}
    style={active ? { backgroundColor: `${colour || '#FFF'}20`, color: colour || '#FFF' } : {}}
  >
    {label}
  </button>
);

const BroadcastChip = ({ label, value }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-white/40 text-[10px] uppercase">{label}</span>
    <span className="text-white text-xs font-black uppercase">{value}</span>
  </div>
);

const ReplayChip = ({ label, value }) => (
  <div>
    <div className="text-white/30 text-[9px] uppercase">{label}</div>
    <div className="text-white text-xs font-bold">{value}</div>
  </div>
);

const HOTKEY_GROUPS = [
  { title: "TRANSPORT", colour: "#EF4444", keys: [
    { key: "Space", action: "Toggle LIVE / STOP" },
    { key: "G", action: "GO LIVE" }, { key: "S", action: "STOP" },
    { key: "R", action: "Reset" }, { key: "P", action: "Replay" },
  ]},
  { title: "PRODUCTION", colour: "#F97316", keys: [
    { key: "F", action: "Freeze frame" }, { key: "O", action: "Toggle overlay" },
    { key: "C", action: "Commentary" }, { key: "I", action: "Signals" },
    { key: "T", action: "Runner tags" }, { key: "K", action: "Chroma (Green/Black)" },
    { key: "X", action: "Toggle controls" },
  ]},
  { title: "FEATURED", colour: "#3B82F6", keys: [
    { key: "1-9", action: "Feature runner" }, { key: "0", action: "Clear" },
  ]},
  { title: "NAV", colour: "#A1A1AA", keys: [
    { key: "H / ?", action: "Hotkeys" }, { key: "Esc", action: "Back" },
  ]},
];

const HotkeyPanel = ({ onClose }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="hotkey-reference-panel">
    <div className="w-full max-w-xl bg-[#0d0f12]/95 border border-white/10 rounded-xl p-6" onClick={e => e.stopPropagation()}>
      <div className="flex items-center gap-2 mb-4">
        <Keyboard className="w-4 h-4 text-[#F97316]" />
        <h2 className="font-heading text-sm font-bold text-white uppercase">Operator Hotkeys</h2>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {HOTKEY_GROUPS.map(g => (
          <div key={g.title}>
            <div className="text-[10px] tracking-widest mb-2" style={{ color: g.colour }}>{g.title}</div>
            {g.keys.map(k => (
              <div key={k.key} className="flex items-center gap-2 mb-1">
                <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white text-[10px] font-mono font-bold min-w-[28px] text-center">{k.key}</kbd>
                <span className="text-white/50 text-xs">{k.action}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default BroadcastOverlayPage;
